import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DashboardLayout from '../components/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { getTeams, deleteTeam } from '../services/teamService';
import { resolveAssetUrl } from '../utils/assetUrl';
import AdminEmptyState from '../components/AdminEmptyState';
import AdminPageHeader from '../components/AdminPageHeader';
import AdminPagination from '../components/AdminPagination';
import AdminSection from '../components/AdminSection';
import useAdminPagination from '../hooks/useAdminPagination';

function TeamPage() {
  const [teams, setTeams] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const {
    page,
    rowsPerPage,
    count,
    paginatedItems: paginatedTeams,
    handlePageChange,
    handleRowsPerPageChange,
  } = useAdminPagination(teams, 10);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const data = await getTeams();
      setTeams(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching teams:', error);
      setTeams([]);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) {
      return;
    }

    try {
      await deleteTeam(id);
      fetchTeams();
    } catch (error) {
      console.error('Error deleting team member:', error);
    }
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <AdminPageHeader
          eyebrow="Operations"
          title="Team"
          description="Manage people, roles, bios, and the display order for the public leadership and team sections."
          meta={<Chip label={`${teams.length} members`} variant="outlined" />}
          action={
            <Button variant="contained" onClick={() => navigate('/team/create')}>
              Add Team Member
            </Button>
          }
        />

        {isMobile ? (
          teams.length ? (
            <Grid container spacing={2}>
              {paginatedTeams.map((team) => (
                <Grid item xs={12} key={team.teamId}>
                  <AdminSection>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          src={team.imageSrc ? resolveAssetUrl(team.imageSrc) : undefined}
                          variant="rounded"
                          sx={{ width: 56, height: 56, borderRadius: 2 }}
                        >
                          {team.name?.charAt(0)}
                        </Avatar>
                        <Stack sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="h6">{team.name}</Typography>
                          <Typography color="text.secondary">{team.role || '-'}</Typography>
                        </Stack>
                      </Stack>
                      <Typography color="text.secondary">
                        {String(team.bio || '').replace(/<[^>]*>/g, '').slice(0, 180) || 'No bio'}
                      </Typography>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="outlined" onClick={() => navigate(`/team/edit/${team.teamId}`)}>
                          Edit
                        </Button>
                        <IconButton color="error" onClick={() => handleDelete(team.teamId)}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </AdminSection>
                </Grid>
              ))}
            </Grid>
          ) : (
            <AdminSection>
              <AdminEmptyState
                title="No team members yet"
                description="Add team entries to keep the people section accurate and current."
              />
            </AdminSection>
          )
        ) : (
          <AdminSection sx={{ p: 0, overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Bio</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teams.length ? (
                  paginatedTeams.map((team) => (
                    <TableRow key={team.teamId}>
                      <TableCell sx={{ fontWeight: 600 }}>{team.name}</TableCell>
                      <TableCell>{team.role || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography noWrap color="text.secondary">
                          {String(team.bio || '').replace(/<[^>]*>/g, '') || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Avatar
                          src={team.imageSrc ? resolveAssetUrl(team.imageSrc) : undefined}
                          variant="rounded"
                          sx={{ width: 44, height: 44, borderRadius: 2 }}
                        >
                          {team.name?.charAt(0)}
                        </Avatar>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          onClick={() => navigate(`/team/edit/${team.teamId}`)}
                          sx={{ mr: 1 }}
                        >
                          Edit
                        </Button>
                        <IconButton color="error" onClick={() => handleDelete(team.teamId)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <AdminEmptyState
                        title="No team members yet"
                        description="Add team entries to keep the people section accurate and current."
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <AdminPagination
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </AdminSection>
        )}
      </Stack>
    </DashboardLayout>
  );
}

export default TeamPage;
